import { useTranslation } from "react-i18next";
import { HandCoins } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { formatNumber } from "../../../../utils";
import { SaleInfo } from "../types";
import { formatDate } from "../utils";
import { DetailList } from "./DetailList";

interface SaleSummaryProps {
  sale: SaleInfo;
  currency: string;
}

export const SaleSummary = ({ sale, currency }: SaleSummaryProps) => {
  const { t } = useTranslation();
  const unit = sale.unit_of_measure ?? "";
  const money = (value: string | null | undefined) =>
    value ? `${currency} ${formatNumber(value)}` : "–";

  const items = [
    {
      label: t("farmersMarket.sale.stage"),
      value: sale.stage ? t(`farmersMarket.sale.stages.${sale.stage}`) : "–",
    },
    {
      label: t("farmersMarket.sale.buyer"),
      value: sale.buyer?.name ?? t("farmersMarket.activity.anonymousBuyer"),
    },
    ...(sale.offer_type
      ? [
          {
            label: t("farmersMarket.sale.offerType"),
            value: t(`farmersMarket.sale.offerTypes.${sale.offer_type}`),
          },
        ]
      : []),
    ...(sale.delivery_status
      ? [
          {
            label: t("farmersMarket.sale.deliveryStatus"),
            value: t(`farmersMarket.sale.deliveryStatuses.${sale.delivery_status}`),
          },
        ]
      : []),
    ...(sale.payment_status
      ? [
          {
            label: t("farmersMarket.sale.paymentStatus"),
            value: t(`farmersMarket.sale.paymentStatuses.${sale.payment_status}`),
          },
        ]
      : []),
    { label: t("farmersMarket.sale.saleDate"), value: formatDate(sale.sale_date) },
    {
      label: t("farmersMarket.sale.finalQuantity"),
      value: sale.final_quantity ? `${formatNumber(sale.final_quantity)} ${unit}` : "–",
    },
    { label: t("farmersMarket.sale.finalUnitPrice"), value: money(sale.final_unit_price) },
    { label: t("farmersMarket.sale.finalValue"), value: money(sale.final_value) },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <HandCoins className="size-4 text-primary" />
          {t("farmersMarket.sale.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <DetailList items={items} />
      </CardContent>
    </Card>
  );
};
